import { TCustomerType, TError, TFetchStatus } from 'models';
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { PageStyled } from '../common/styles';
import IDDList from './List';
import IDDProfile from './Profile';
import {
  errorNotification,
  getQueryParam,
  isIframe,
  makeRequestQuery,
  preflightAPICall,
  processAPIError,
  setListPage,
  translate
} from 'utils';
import axios from 'axios';
import { API_IDD_QUESTIONNAIRE_URL } from 'app/Components/IDD';
import { setListPageSize } from '../common/List';
import { getFile } from 'utils/getFile';
import { useApp } from 'context/App';
import apiConfig from 'config/api';
import BulkUpload from '../common/QRBulkUpload';
import { TIDDSectionStep } from './models';
import { useAuth } from 'context/Auth';
import {
  POST_EVENT_GET_POLICIES_FOR_IDD_LIST,
  POST_EVENT_OPEN_POLICY_BY_INSLY_ID,
  POST_EVENT_SET_POLICIES_FOR_IDD_LIST
} from 'config/consts';

export const LIST_IDD_NAME = 'idd';

export type TPoliciesWithLink = { id: string, policy_numbers: Record<string, number> }[];

export type TIDD = {
  external?: boolean,
  customer: {
    type: TCustomerType,
    props: {
      company_type?: string,
    },
  },
  comment?: string,
  policy_numbers?: string[],
  schema_path: string,
  number: string,
  id: string,
  quote?: {
    id: string,
    number: string,
  },
  created_by_identity?: {
    name: string,
  },
  customer_data: {
    id: string,
    revision_id: string,
    name: string,
    pesel?: string,
    idcode?: string,
    regon?: string,
    phone?: string,
    email?: string,
  },
  quote_id?: string | null,
  answers: Record<string, any>,
  status: number,
  sent_at?: string | null,
  sent_by?: string | null,
  approved_at?: string | null,
  verification_code?: string | null,
  created_at: string,
  created_by?: string | null,
  updated_by?: string | null,
  updated_at?: string | null,
};

export interface IPageIDD {
  title: string,
  listData: {
    count?: number,
    total_results?: number,
    total_pages?: number,
    last_page?: number,
    current_page?: number,
    page?: number,
    results?: TIDD[],
    total?: number,
    errors?: TError[],
  },
}

const PageIDD = () => {
  const [dataFetchStatus, updateDataFetchStatus] = useState<TFetchStatus>('loading');
  const [iddErrorText, updateIddErrorText] = useState('');
  const [iddList, updateIddList] = useState<IPageIDD['listData']>({});
  const [searchQuery, updateSearchQuery] = useState<string>();
  const [page, updatePage] = useState<number>();
  const [status, updateStatus] = useState<string>();
  const [createdFrom, updateCreatedFrom] = useState<string>();
  const [createdTo, updateCreatedTo] = useState<string>();
  const [selectedIDD, updateSelectedIDD] = useState<TIDD | undefined>(undefined);
  const [domain] = useState<string | null>(getQueryParam('domain'));
  const [policiesWithLink, updatePoliciesWithLink] = useState<TPoliciesWithLink>([]);

  const navigate = useNavigate();
  let location = useLocation();
  const { userProps } = useAuth();

  const { showNotification } = useApp();

  const iddParams = useParams<{ section: TIDDSectionStep, iddId: string }>();
  const section = iddParams.section as TIDDSectionStep;
  const iddId = iddParams.iddId;

  useEffect(() => {
    setListPage(page, updatePage);
  });

  useEffect(() => {
    updateDataFetchStatus(null);

    if (section === 'list' && page) {
      updateSelectedIDD(undefined);
      updateIddList({});
      getList();
    }

    if (section === 'profile') {
      if (!selectedIDD) {
        getItem();
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, page]);

  useEffect(() => {
    if (section === 'list' && (
      typeof searchQuery !== 'undefined'
      || typeof status !== 'undefined'
      || typeof createdFrom !== 'undefined'
      || typeof createdTo !== 'undefined'
    )) {
      navigate(`${location.pathname}`, { replace: true });

      if (!page || page === 1) {
        getList();
      } else {
        updatePage(1);
      }

    } else {
      setListPage(page, updatePage);
    }
  }, [searchQuery, status, createdFrom, createdTo]);

  useEffect(() => {
    const handler = (event: { data: any }) => {
      if (event.data) {
        setPoliciesLinks(event.data, updatePoliciesWithLink);
      }
    };

    window.addEventListener('message', handler);

    // clean up
    return () => window.removeEventListener('message', handler);

    // eslint-disable-next-line
  }, []); // empty array => run only once

  const handleSearchChange = (value: string) => {
    updateSearchQuery(value);
  };

  const getList = () => {
    updateDataFetchStatus('loading');
    let query: Record<string, string | number> = {};

    query.Size = setListPageSize();

    if (page) {
      query.Page = page;
    }

    if (searchQuery) {
      query.Search = searchQuery.trim();
    }

    if (status) {
      query.Status = status;
    }

    if (createdFrom) {
      query.CreatedFrom = createdFrom;
    }

    if (createdTo) {
      query.CreatedTo = createdTo;
    }

    if (domain) {
      query.Domain = domain;
    }

    preflightAPICall(() => {
      axios.post(apiConfig.QMT_SEARCH, {
        'data': query,
        'name': 'idd-search'
      }).then(response => {
        const data = response.data;
        onDataLoaded(data);

      }).catch(error => {

        onDataLoaded({
          results: [],
        });

        updateIddErrorText(() => processAPIError(error) as string);

      });
    });

  };

  const getItem = () => {
    updateDataFetchStatus('loading');
    preflightAPICall(() => {
      axios.get(`${API_IDD_QUESTIONNAIRE_URL}/${iddId}`).then(response => {
        const data = response.data;
        updateSelectedIDD(data);
        updateDataFetchStatus('success');
      }).catch(error => {
        updateIddErrorText(() => processAPIError(error) as string);
        updateDataFetchStatus('failed');
      });
    });
  };

  const onDataLoaded = (data: IPageIDD['listData']) => {
    updateIddList(data);
    updateDataFetchStatus('success');
    isIframe() && getPoliciesLinks(data.results || []);
  };

  const handleAddIDDClick = () => navigate(`/idd/add`);

  const handleSaveIdd = (idd?: TIDD) => {
    if (idd) {
      updateSelectedIDD(idd);
      navigate(`/idd/profile/${idd.id}`, { replace: true });
    }
  };

  const handleCSVDownload = () => {
    if (dataFetchStatus === 'loading') {
      return;
    }

    updateDataFetchStatus('loading');
    let query: Record<string, string | number> = {};

    if (searchQuery) {
      query.search = searchQuery;
    }

    const queryString = makeRequestQuery(query);

    preflightAPICall(() => {
      getFile({
        method: 'post',
        data: {
          search: searchQuery,
          status,
          created_from: createdFrom,
          created_to: createdTo,
        },
        url: `${API_IDD_QUESTIONNAIRE_URL}/download/csv${queryString}`,
        fileName: 'APK-report.csv',
        onError: (error) => {
          errorNotification(error, showNotification);
          updateDataFetchStatus('success');
        },
        onSuccess: () => {
          updateDataFetchStatus('success');
        }
      });
    });
  };

  const updateFilters = (name?: string, value?: string) => {
    switch (name) {
      case 'status':
        updateStatus(value ? value : '');
        break;
      case 'createdFrom':
        // bug in DatePicker it returns '' on month change and it calls getList by useEffect
        if (value === '' && (typeof createdFrom === 'undefined' || createdFrom === '')) {
          return;
        }
        updateCreatedFrom(value);
        break;
      case 'createdTo':
        // bug in DatePicker it returns '' on month change and it calls getList by useEffect
        if (value === '' && (typeof createdTo === 'undefined' || createdTo === '')) {
          return;
        }
        updateCreatedTo(value);
        break;
    }
  };

  const handleListChangePage = (page: number) => {
    navigate(`${location.pathname}?page=${page}`, { replace: true });

    updatePage(page);
  };

  const handleScanIDDClick = () => {
    navigate(`/idd/bulk_upload`);
  };

  switch (section) {
    case 'add':
      return (
        <IDDProfile
          handleIddSave={(idd) => handleSaveIdd(idd)}
          initialIDDState="idd_requested"
        />
      );
    case 'profile':
      if (selectedIDD) {
        return (
          <IDDProfile
            customerData={selectedIDD.customer_data}
            idd={selectedIDD}
            showOnly={true}
            initialIDDState="exist"
            handleIddSave={(idd) => handleSaveIdd(idd)}
            handleIddCancel={() => navigate(-1)}
          />
        );
      } else {
        return null;
      }
    case 'bulk_upload':
      return (
        <BulkUpload
          apiURL={`${apiConfig.IDD}/file/answers/signed`}
          goBackButtonTranslation="idd.mass_upload_qr_docs.button.back"
          successTranslation="idd.documents.mass_upload_qr_docs.success"
          uploaderTranslation="idd.documents.mass_upload_qr_docs.uploader"
        />
      );
    case 'list':
    default:
      return (
        <PageStyled>
          <IDDList
            title={translate({ key: 'idd.list.title' })}
            errorText={iddErrorText}
            step={section}
            dataStatus={dataFetchStatus}
            itemList={iddList}
            policiesWithLink={policiesWithLink}
            handleSearchChange={handleSearchChange}
            handleListChangePage={handleListChangePage}
            handleAddIDDClick={handleAddIDDClick}
            handleCSVDownload={handleCSVDownload}
            filters={{ status, createdFrom: createdFrom, createdTo: createdTo }}
            updateFilter={updateFilters}
            userFilters={userProps?.custom_list_filters?.[LIST_IDD_NAME] || []}
            handleScanIDDClick={handleScanIDDClick}
            updateDataFetchStatus={updateDataFetchStatus}
            updateIddErrorText={updateIddErrorText}
            updateIddList={updateIddList}
          />
        </PageStyled>
      );
  }

};

export const getPoliciesLinks = (idds: TIDD[]) => {
  let iddsPolicies: {
    id: string,
    policy_numbers: string[],
    created_at: string,
  }[] = [];

  idds?.forEach(idd => {
    if (idd.policy_numbers?.length) {
      iddsPolicies.push({
        id: idd.id,
        policy_numbers: idd.policy_numbers,
        created_at: idd.created_at,
      });
    }
  });

  if (iddsPolicies.length) {
    window.parent?.postMessage({ name: POST_EVENT_GET_POLICIES_FOR_IDD_LIST, idds: iddsPolicies }, '*');
  }

};

export const setPoliciesLinks = (data: Record<string, any>, updatePoliciesWithLink: (idds: TPoliciesWithLink) => void) => {
  if (data.name === POST_EVENT_SET_POLICIES_FOR_IDD_LIST) {
    updatePoliciesWithLink(data.idds);
  }
};

export const processPolicyNumberClick = (insly3Id?: number) => {
  window.parent?.postMessage({ name: POST_EVENT_OPEN_POLICY_BY_INSLY_ID, policy_oid: insly3Id }, '*');
};

export default PageIDD;
